import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../lib/api";
import VideoCard from "../components/VideoCard";
import PageHeroBanner from "../components/PageHeroBanner";
import { FILMS_HOTSPOTS, WEATHER_HOTSPOTS } from "../components/heroBannerHotspots";

// Map category slug → page hero banner config. Only categories with a
// dedicated banner show one; all others fall back to the simple text header.
const CATEGORY_BANNERS = {
  films: {
    src: "/tapin-films-hero.png",
    alt: "Independent Films Live Here — Tap IN Television Network",
    testid: "films-page-hero",
    hotspots: FILMS_HOTSPOTS,
  },
  weather: {
    src: "/tapin-weather-hero.png",
    alt: "Tap IN TV Weather — Your Weather. Your World.",
    testid: "weather-page-hero",
    hotspots: WEATHER_HOTSPOTS,
  },
};

export default function Browse() {
  const { category } = useParams();
  const [videos, setVideos] = useState([]);
  const [live, setLive] = useState([]);
  const [cat, setCat] = useState(null);
  const [sort, setSort] = useState("recent");

  useEffect(() => {
    api.get(`/videos?category=${category}&sort=${sort}&limit=60`).then((r) => setVideos(r.data));
    // Live streams in this category surface alongside VOD so fresh broadcasts
    // are never hidden from the category page.
    api.get(`/live?category=${category}`).then((r) => setLive(r.data || [])).catch(() => setLive([]));
    api.get("/categories").then((r) => setCat((r.data || []).find((c) => c.slug === category)));
  }, [category, sort]);

  const banner = CATEGORY_BANNERS[category];

  return (
    <>
      {banner && <PageHeroBanner {...banner} />}
      <div data-testid="browse-page" className={`${banner ? "pt-2" : "pt-32"} px-4 sm:px-8 md:px-12 pb-20 max-w-7xl mx-auto`}>
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-tv-accent font-semibold">Category</div>
          <h1 className="font-display font-black text-5xl text-white tracking-tighter mt-2">{cat?.name || category}</h1>
        </div>
        <div className="flex gap-2">
          {["recent", "trending"].map((s) => (
            <button
              key={s}
              data-testid={`sort-${s}`}
              onClick={() => setSort(s)}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition ${sort === s ? "bg-tv-accent text-black" : "tv-glass text-white hover:bg-white/10"}`}
            >
              {s === "recent" ? "Newest" : "Trending"}
            </button>
          ))}
        </div>
      </div>

      {/* Live now in this category — always above VOD so creators get
          maximum real-time exposure. */}
      {live.length > 0 && (
        <section data-testid="browse-live-row" className="mb-10">
          <div className="flex items-center gap-2 mb-4">
            <span className="bg-tv-live text-white text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded tv-pulse">● LIVE</span>
            <h2 className="font-display font-black text-2xl text-white">On Air Now</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {live.map((s) => <VideoCard key={s.id} item={s} variant="wide" live fullWidth />)}
          </div>
        </section>
      )}

      {videos.length === 0 ? (
        <div className="text-white/60 text-center py-24">
          {live.length > 0 ? "No VOD in this category yet — check back soon." : "No content in this category yet."}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {videos.map((v) => <VideoCard key={v.id} item={v} fullWidth />)}
        </div>
      )}
    </div>
    </>
  );
}
